import { useEffect, useMemo } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { AlertCircle, Check, ChevronDown, ClipboardList, MessageSquare, Target } from "lucide-react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { WorkItemRow } from "@/components/my-work/WorkItemRow"
import { CommentWorkRow } from "@/components/my-work/CommentWorkRow"
import { useMyWorkStore, type MyWorkCommentItem, type MyWorkItem } from "@/stores/my-work-store"
import { useBidsStore } from "@/stores/bids-store"
import { COLLABORATION_STATUSES } from "@/lib/section-status"
import { groupMyWork, type MyWorkBidGroup, type MyWorkSortMode } from "@/lib/my-work-sort"
import { fromMyWorkState, type FromMyWorkState } from "@/lib/my-work-row"
import { daysUntilDeadline, isActiveBidStatus, isDeadlinePast, withDeadlineTime } from "@/lib/bid-deadline"
import { shortDate } from "@/lib/datetime"
import { bidIsClosingSoon, bidIsOverdue } from "@/lib/my-work-counts"
import { cn } from "@/lib/utils"
import type { Bid } from "@/lib/schemas"

// ===================
// Types
// ===================

type WorkTab = 'assigned' | 'review' | 'comments'
type UrgencyFilter = 'all' | 'overdue' | 'closing'
type SortMode = MyWorkSortMode

const SORT_LABELS: Record<SortMode, string> = {
  urgency: 'Urgency',
  status: 'Status',
  bid: 'Bid',
}

// ===================
// Deadline helpers (bid-level)
// ===================

interface DeadlinePill {
  label: string
  tone: 'danger' | 'warning' | 'neutral'
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/**
 * `Closed 18 Sep · 5 days ago` (red), `Closes 7 Oct · in 13 days` (amber, within
 * 14 days), `Closes 7 Oct` (neutral). A deadline with a time carries it
 * (`Closes 7 Oct, 2:00 pm AEDT · today`) and reads closed from that moment
 * (`Closed 7 Oct, 2:00 pm AEDT · today`). Nothing for bids whose deadline no
 * longer matters (submitted, decided, archived).
 */
function getDeadlinePill(bid: Bid | undefined, now: Date = new Date()): DeadlinePill | null {
  if (!bid?.deadline || bid.archived || !isActiveBidStatus(bid.status)) return null
  const days = daysUntilDeadline(bid.deadline, now)
  if (days === null) return null
  const date = withDeadlineTime(shortDate(bid.deadline), bid)
  if (days < 0) return { label: `Closed ${date} · ${plural(Math.abs(days), 'day')} ago`, tone: 'danger' }
  if (isDeadlinePast(bid, now)) return { label: `Closed ${date} · today`, tone: 'danger' }
  if (days === 0) return { label: `Closes ${date} · today`, tone: 'warning' }
  if (days <= 14) return { label: `Closes ${date} · in ${plural(days, 'day')}`, tone: 'warning' }
  return { label: `Closes ${date}`, tone: 'neutral' }
}

// ===================
// Summaries
// ===================

const IN_PROGRESS_STATUSES = new Set<string>(['in_progress', 'changes_requested'])
const COMPLETE_STATUSES = new Set<string>(['draft_complete', 'in_review', 'approved', 'complete'])

function groupProgress(items: MyWorkItem[]): string {
  let notStarted = 0
  let inProgress = 0
  let complete = 0
  for (const i of items) {
    if (i.collaborationStatus === 'not_started') notStarted += 1
    else if (IN_PROGRESS_STATUSES.has(i.collaborationStatus)) inProgress += 1
    else if (COMPLETE_STATUSES.has(i.collaborationStatus)) complete += 1
  }
  // Every row is counted somewhere: "not started" only appears when it has rows.
  const parts = notStarted > 0 ? [`${notStarted} not started`] : []
  parts.push(`${inProgress} in progress`, `${complete} complete`)
  return parts.join(' · ')
}

// ===================
// Group
// ===================

const PILL_TONE: Record<DeadlinePill['tone'], string> = {
  danger: "bg-destructive/10 text-destructive",
  warning: "bg-[hsl(var(--status-warning)/0.16)] text-[hsl(38_85%_28%)]",
  neutral: "bg-muted text-muted-foreground",
}

function GroupBlock({
  group, isFirst, bid, linkState,
}: {
  group: MyWorkBidGroup
  isFirst: boolean
  bid: Bid | undefined
  linkState: FromMyWorkState
}) {
  const pill = getDeadlinePill(bid)

  return (
    <div className={!isFirst ? 'border-t border-border' : ''} data-testid="my-work-group">
      <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
        <Target className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <Link
          to={`/app/bids/${group.bidId}`}
          className="truncate text-[13px] font-semibold text-foreground hover:text-[hsl(var(--link))] transition-colors"
          data-testid="group-title"
        >
          {group.bidTitle}
        </Link>
        {bid?.client && (
          <span className="hidden sm:inline truncate text-meta" data-testid="group-client">{bid.client}</span>
        )}
        <span className="ml-auto flex items-center gap-3 shrink-0">
          <span className="hidden sm:inline text-meta tabular-nums" data-testid="group-progress">
            {groupProgress(group.items)}
          </span>
          {pill && (
            <span
              className={cn("rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap", PILL_TONE[pill.tone])}
              data-testid="deadline-pill"
              data-tone={pill.tone}
            >
              {pill.label}
            </span>
          )}
        </span>
      </div>
      <div className="divide-y divide-border">
        {group.items.map((item) => (
          <WorkItemRow key={item.id} item={item} linkState={linkState} testId="my-work-row" />
        ))}
      </div>
    </div>
  )
}

// ===================
// Comments tab
// ===================

interface CommentBidGroup {
  bidId: string
  bidTitle: string
  items: MyWorkCommentItem[]
}

/** Group by bid, keeping the server's newest-first order within and across groups. */
function groupComments(items: MyWorkCommentItem[]): CommentBidGroup[] {
  const groups = new Map<string, CommentBidGroup>()
  for (const item of items) {
    const group = groups.get(item.bidId)
    if (group) group.items.push(item)
    else groups.set(item.bidId, { bidId: item.bidId, bidTitle: item.bidTitle, items: [item] })
  }
  return [...groups.values()]
}

function CommentGroupBlock({
  group, isFirst, bid, linkState,
}: {
  group: CommentBidGroup
  isFirst: boolean
  bid: Bid | undefined
  linkState: FromMyWorkState
}) {
  const pill = getDeadlinePill(bid)

  return (
    <div className={!isFirst ? 'border-t border-border' : ''} data-testid="my-work-comment-group">
      <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
        <Target className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <Link
          to={`/app/bids/${group.bidId}`}
          className="truncate text-[13px] font-semibold text-foreground hover:text-[hsl(var(--link))] transition-colors"
          data-testid="group-title"
        >
          {group.bidTitle}
        </Link>
        {bid?.client && (
          <span className="hidden sm:inline truncate text-meta">{bid.client}</span>
        )}
        {pill && (
          <span
            className={cn("ml-auto rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap shrink-0", PILL_TONE[pill.tone])}
            data-testid="deadline-pill"
            data-tone={pill.tone}
          >
            {pill.label}
          </span>
        )}
      </div>
      <div className="divide-y divide-border">
        {group.items.map((item) => (
          <CommentWorkRow key={item.commentId} item={item} linkState={linkState} />
        ))}
      </div>
    </div>
  )
}

function RowSkeletons() {
  return (
    <div className="rounded-xl border bg-card shadow-sm overflow-hidden divide-y divide-border" data-testid="my-work-skeleton">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-2.5">
          <Skeleton className="h-5 w-24 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-8 w-[5.5rem]" />
        </div>
      ))}
    </div>
  )
}

// ===================
// Filter trigger
// ===================

function SingleSelect({
  label, value, options, onChange, testId, className,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
  testId: string
  /** Trigger classes — e.g. a max width so a long value truncates instead of widening the toolbar. */
  className?: string
}) {
  const active = options.find((o) => o.value === value)
  const text = `${label}: ${active?.label ?? 'All'}`
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-8 gap-1.5 text-xs font-normal", className)}
          title={text}
          data-testid={testId}
        >
          <span className="min-w-0 truncate">{text}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onClick={() => onChange(o.value)} className="text-xs gap-2">
            <Check className={cn("h-3.5 w-3.5", o.value === value ? "opacity-100" : "opacity-0")} />
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ===================
// Page
// ===================

export function MyWorkPage() {
  const { items, comments, commentsError, isLoading, error, fetchMyWork } = useMyWorkStore()
  const bids = useBidsStore((s) => s.bids)
  const ensureAllBids = useBidsStore((s) => s.ensureAllBids)
  const [searchParams, setSearchParams] = useSearchParams()

  // Stable action refs only — see the note in HomePage (F8).
  useEffect(() => {
    void fetchMyWork()
    void ensureAllBids()
  }, [fetchMyWork, ensureAllBids])

  const tabParam = searchParams.get('tab')
  const tab: WorkTab = tabParam === 'review' ? 'review' : tabParam === 'comments' ? 'comments' : 'assigned'
  const urgencyFilter: UrgencyFilter =
    searchParams.get('filter') === 'overdue' ? 'overdue'
    : searchParams.get('filter') === 'closing' ? 'closing'
    : 'all'
  const statusFilter = searchParams.get('status') ?? 'all'
  const bidFilter = searchParams.get('bid') ?? 'all'
  const sortMode: SortMode = (['urgency', 'status', 'bid'] as const).find((m) => m === searchParams.get('sort')) ?? 'urgency'

  const setParam = (key: string, value: string, defaultValue: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === defaultValue) next.delete(key)
    else next.set(key, value)
    setSearchParams(next, { replace: true })
  }

  const bidsById = useMemo(() => new Map(bids.map((b) => [b.id, b])), [bids])

  const assignedItems = useMemo(() => items.filter((i) => i.role === 'writer'), [items])
  const reviewItems = useMemo(() => items.filter((i) => i.role === 'reviewer'), [items])
  const tabItems = tab === 'review' ? reviewItems : assignedItems

  const filtered = useMemo(() => {
    return tabItems.filter((i) => {
      if (statusFilter !== 'all' && i.collaborationStatus !== statusFilter) return false
      if (bidFilter !== 'all' && i.bidId !== bidFilter) return false
      const bid = bidsById.get(i.bidId)
      if (urgencyFilter === 'overdue' && !bidIsOverdue(bid)) return false
      if (urgencyFilter === 'closing' && !bidIsClosingSoon(bid)) return false
      return true
    })
  }, [tabItems, statusFilter, bidFilter, urgencyFilter, bidsById])

  const groups = useMemo(() => groupMyWork(filtered, sortMode, bidsById), [filtered, sortMode, bidsById])

  const bidOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const i of tabItems) if (!seen.has(i.bidId)) seen.set(i.bidId, i.bidTitle)
    return [{ value: 'all', label: 'All' }, ...Array.from(seen, ([value, label]) => ({ value, label }))]
  }, [tabItems])

  const hasActiveFilters = statusFilter !== 'all' || bidFilter !== 'all' || urgencyFilter !== 'all'

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('status')
    next.delete('bid')
    next.delete('filter')
    setSearchParams(next, { replace: true })
  }

  // Summary line — counts what is on screen, so it moves with the filters.
  const summarySegments = useMemo(() => {
    const segments: Array<{ label: string; danger?: boolean }> = []
    segments.push({
      label: hasActiveFilters
        ? `Showing ${filtered.length} of ${tabItems.length}`
        : plural(tabItems.length, 'section'),
    })
    const counts: Record<string, number> = {}
    let overdue = 0
    for (const i of filtered) {
      counts[i.collaborationStatus] = (counts[i.collaborationStatus] ?? 0) + 1
      if (bidIsOverdue(bidsById.get(i.bidId))) overdue += 1
    }
    if (overdue > 0) segments.push({ label: `${overdue} overdue`, danger: true })
    for (const s of COLLABORATION_STATUSES) {
      const n = counts[s.key] ?? 0
      if (n > 0) segments.push({ label: `${n} ${s.label.toLowerCase()}` })
    }
    return segments
  }, [filtered, tabItems.length, hasActiveFilters, bidsById])

  const rowLinkState = useMemo(() => fromMyWorkState(searchParams.toString()), [searchParams])

  const commentGroups = useMemo(() => groupComments(comments), [comments])

  // Skeleton only on the first load — a background refetch keeps the list up.
  const showSkeleton = isLoading && items.length === 0 && comments.length === 0

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="w-full">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={(v) => setParam('tab', v, 'assigned')}>
              <TabsList className="bg-muted p-1 h-9 rounded-lg">
                <TabsTrigger
                  value="assigned"
                  className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
                >
                  Assigned to me
                  <span className="ml-1.5 rounded-md bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">
                    {assignedItems.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="review"
                  className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
                >
                  Awaiting my review
                  <span className="ml-1.5 rounded-md bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">
                    {reviewItems.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="comments"
                  className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
                >
                  Comments
                  <span className="ml-1.5 rounded-md bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">
                    {comments.length}
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {tab !== 'comments' && (
              <div className="flex items-center gap-2" data-testid="my-work-toolbar">
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
                <SingleSelect
                  label="Status"
                  value={statusFilter}
                  onChange={(v) => setParam('status', v, 'all')}
                  testId="filter-status"
                  options={[
                    { value: 'all', label: 'All' },
                    ...COLLABORATION_STATUSES.map((s) => ({ value: s.key, label: s.label })),
                  ]}
                />
                <SingleSelect
                  label="Bid"
                  value={bidFilter}
                  onChange={(v) => setParam('bid', v, 'all')}
                  testId="filter-bid"
                  className="max-w-48"
                  options={bidOptions}
                />
                <SingleSelect
                  label="Sort"
                  value={sortMode}
                  onChange={(v) => setParam('sort', v, 'urgency')}
                  testId="filter-sort"
                  options={(['urgency', 'status', 'bid'] as const).map((m) => ({ value: m, label: SORT_LABELS[m] }))}
                />
              </div>
            )}
          </div>

          {tab === 'comments' && !showSkeleton && !error && comments.length > 0 && (
            <p className="mt-2 text-meta" data-testid="summary-line">
              {plural(comments.length, 'comment')} waiting on you
            </p>
          )}

          {tab !== 'comments' && !showSkeleton && !error && tabItems.length > 0 && (
            <p className="mt-2 text-meta" data-testid="summary-line">
              {summarySegments.map((s, i) => (
                <span key={s.label}>
                  {i > 0 && ' · '}
                  <span className={cn(s.danger && "text-destructive font-medium")}>{s.label}</span>
                </span>
              ))}
            </p>
          )}

          <div className="mt-4">
            {showSkeleton ? (
              <RowSkeletons />
            ) : error ? (
              <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            ) : tab === 'comments' ? (
              commentsError ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Failed to load comments
                </div>
              ) : comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                    <MessageSquare className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1">No comments waiting on you</h3>
                  <p className="text-[13px] text-muted-foreground max-w-xs">
                    When someone mentions you, or replies to a comment you started, it will appear here.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                  {commentGroups.map((group, i) => (
                    <CommentGroupBlock
                      key={group.bidId}
                      group={group}
                      isFirst={i === 0}
                      bid={bidsById.get(group.bidId)}
                      linkState={rowLinkState}
                    />
                  ))}
                </div>
              )
            ) : tabItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                  <ClipboardList className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-1">
                  {tab === 'review' ? 'No reviews waiting on you' : 'Nothing assigned to you'}
                </h3>
                <p className="text-[13px] text-muted-foreground max-w-xs">
                  {tab === 'review'
                    ? 'When a writer requests your review on a section, it will appear here.'
                    : "When you're assigned to sections in a bid response, they'll appear here."}
                </p>
              </div>
            ) : groups.length === 0 ? (
              <p className="py-16 text-center text-sm text-muted-foreground">No sections match these filters</p>
            ) : (
              <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                {groups.map((group, i) => (
                  <GroupBlock
                    key={group.bidId}
                    group={group}
                    isFirst={i === 0}
                    bid={bidsById.get(group.bidId)}
                    linkState={rowLinkState}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
